import { AsyncPipe, LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { LocalizationScopeDirective, LocalizePipe } from '@capital-access/common/localization';
import { FireflyDrawerContent, NotificationService } from '@capital-access/firefly/components';
import { NotificationsDrawerActions } from '../../+store/notifications-drawer.actions';
import {
  selectCategories,
  selectError,
  selectLoading,
  selectTypePageState
} from '../../+store/notifications-drawer.selectors';
import { NotificationTypePageState } from '../../+store/notifications-drawer.state';
import { NOTIFICATIONS_LOCALIZATION_SCOPE, UNDO_DELETE_DELAY_MS } from '../../constants/notifications-drawer.constants';
import { NotificationCategoryDto } from '../../models/notifications-grouped.model';
import { NotificationTypeListComponent } from '../notification-type-list/notification-type-list.component';

@Component({
  selector: 'ca-notifications-drawer',
  templateUrl: './notifications-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    LowerCasePipe,
    NgbNavModule,
    LocalizationScopeDirective,
    LocalizePipe,
    NotificationTypeListComponent
  ]
})
export class NotificationsDrawerComponent extends FireflyDrawerContent<Record<string, never>> implements OnInit {
  private store = inject(Store);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChildren('undoToastTemplate') undoToastTemplates!: QueryList<TemplateRef<unknown>>;
  pendingDeletes: Array<{ notificationId: number }> = [];
  // Maps each pending notificationId to its specific TemplateRef so that
  // concurrent toasts each bind to the correct undo action.
  private toastTemplateMap = new Map<number, TemplateRef<unknown>>();
  // Tracks auto-cleanup timers so local state is removed after the toast duration.
  private cleanupTimers = new Map<number, ReturnType<typeof setTimeout>>();

  categories$: Observable<NotificationCategoryDto[]> = this.store.select(selectCategories);
  loading$: Observable<boolean> = this.store.select(selectLoading);
  error$: Observable<string | null> = this.store.select(selectError);

  activeTabId: string | null = null;
  readonly localizationScope = NOTIFICATIONS_LOCALIZATION_SCOPE;

  ngOnInit(): void {
    this.store.dispatch(NotificationsDrawerActions.loadNotifications());
  }

  getTypePageState(typeCode: string): Observable<NotificationTypePageState | undefined> {
    return this.store.select(selectTypePageState(typeCode));
  }

  onLoadMore(typeCode: string): void {
    this.store.dispatch(NotificationsDrawerActions.loadMoreForType({ typeCode }));
  }

  onMarkAsRead(event: { notificationId: number; typeCode: string }): void {
    this.store.dispatch(NotificationsDrawerActions.markAsRead({ notificationId: event.notificationId }));
  }

  onDelete(event: { notificationId: number; typeCode: string; notificationTitle: string }): void {
    this.pendingDeletes = [...this.pendingDeletes, { notificationId: event.notificationId }];
    this.cdr.detectChanges();
    // Rebuild the map after detectChanges() so the QueryList reflects the newly
    // rendered template. Keying by notificationId (not by QueryList position)
    // ensures each concurrent toast resolves to the correct undo action.
    this.undoToastTemplates.toArray().forEach((tpl, i) => {
      this.toastTemplateMap.set(this.pendingDeletes[i].notificationId, tpl);
    });
    const template = this.toastTemplateMap.get(event.notificationId);
    if (template) {
      this.notificationService.notify(template, {
        type: 'default',
        headerText: event.notificationTitle,
        delay: UNDO_DELETE_DELAY_MS
      });
    }
    this.store.dispatch(
      NotificationsDrawerActions.softDeleteNotification({
        notificationId: event.notificationId,
        typeCode: event.typeCode
      })
    );
    // Schedule cleanup of local template state after the toast expires.
    // If undo is clicked the timer is cancelled in onUndoToast().
    this.cleanupTimers.set(
      event.notificationId,
      setTimeout(() => this.cleanPendingEntry(event.notificationId), UNDO_DELETE_DELAY_MS)
    );
  }

  private cleanPendingEntry(notificationId: number): void {
    this.pendingDeletes = this.pendingDeletes.filter(p => p.notificationId !== notificationId);
    this.toastTemplateMap.delete(notificationId);
    this.cleanupTimers.delete(notificationId);
  }

  onUndoToast(notificationId: number): void {
    const timer = this.cleanupTimers.get(notificationId);
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    this.cleanPendingEntry(notificationId);
    this.store.dispatch(NotificationsDrawerActions.undoDelete({ notificationId }));
  }

  onRetry(): void {
    this.store.dispatch(NotificationsDrawerActions.loadNotifications());
  }

  navigateToDataAlerts(): void {
    this.close();
    this.router.navigateByUrl('/data-alerts');
  }

  trackByCategory(_: number, cat: NotificationCategoryDto): string {
    return cat.category;
  }
}
